import { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { COURT_LEVEL } from "@demandproai/shared";
import Constants from "../Constants";
import useAxios from "./useAxios";
import { toaster } from "../utils/toast";

const courtLevelValues = Object.values(COURT_LEVEL);

const courtLevelOptions = courtLevelValues.map((value) => ({
   value,
   text: value.charAt(0).toUpperCase() + value.slice(1),
}));

const emptyCourt = { name: "", longName: "", judges: [], level: "" };

const toJudgeItems = (judges) =>
   Array.isArray(judges) ? judges.map((name) => ({ _id: name, name })) : [];

const useAddEditCourt = ({ selectedCourt, onSuccess } = {}) => {
   const { postData } = useAxios();
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [judgeItems, setJudgeItems] = useState([]);

   const validationSchema = useMemo(
      () =>
         Yup.object().shape({
            name: Yup.string().trim().required("Name is required").max(500, "Name is too long"),
            longName: Yup.string().trim().required("Long name is required").max(1000, "Long name is too long"),
            judges: Yup.array().of(Yup.object()).nullable(),
            level: Yup.string().required("Level is required").oneOf(courtLevelValues, "Invalid level"),
         }),
      []
   );

   const onJudgeSearch = useCallback((text) => {
      const trimmed = (text || "").trim();
      setJudgeItems(trimmed ? [{ _id: trimmed, name: trimmed }] : []);
   }, []);

   const submitCourt = useCallback(
      async (values) => {
         setIsSubmitting(true);
         try {
            const url = selectedCourt?._id
               ? `${Constants.ApiUrl.court.createEditCourt}/${selectedCourt._id}`
               : Constants.ApiUrl.court.createEditCourt;

            const payload = {
               name: values.name.trim(),
               longName: values.longName.trim(),
               judges: values.judges.map((judge) => judge.name),
               level: values.level,
            };

            const { success, message } = await postData(url, payload);
            if (!success) {
               toaster({ message: message || "Failed to save court", success: false });
               return;
            }

            toaster({ message, success: true });
            onSuccess?.();
         } finally {
            setIsSubmitting(false);
         }
      },
      [postData, selectedCourt, onSuccess]
   );

   const formik = useFormik({
      initialValues: {
         name: selectedCourt?.name ?? emptyCourt.name,
         longName: selectedCourt?.longName ?? emptyCourt.longName,
         judges: toJudgeItems(selectedCourt?.judges),
         level: selectedCourt?.level ?? emptyCourt.level,
      },
      enableReinitialize: true,
      validationSchema,
      onSubmit: submitCourt,
   });

   const getErrorMessage = useCallback(
      (field) => formik.touched[field] && formik.errors[field],
      [formik.touched, formik.errors]
   );

   return {
      formik,
      isSubmitting,
      courtLevelOptions,
      getErrorMessage,
      judgeItems,
      onJudgeSearch,
   };
};

export default useAddEditCourt;
